import React, {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import Typography from "../Typography/Typography";
import Button from "../Button/Button";
import XIcon from "../../assets/icons/bx-x-gray.svg";
import XIconDarkGrey from "../../assets/icons/bx-x-dark-grey.svg";
import GridSMHidden from "../GridComponent/components/GridSMHidden";
import useSendInvitations from "../../store/Mutations/useSendInvitation";
import useOrganisation from "../../store/Querys/useOrganisation";
import { useToast } from "../../context/ToastProvider";
import useSingleOrganisation from "../../store/Querys/useSingleOrganization";
import useInvitedStudents from "../../store/Querys/useInvitations";

const InviteStudentsBox = ({
  onSuccess,
  cancelClick,
}: {
  onSuccess?: () => void;
  cancelClick?: () => void;
}) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [notEnoughLicenses, setNotEnoughLicenses] = useState(false);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hasShownLicenseWarning, setHasShownLicenseWarning] = useState(false);
  const [currentToastKey, setCurrentToastKey] = useState<string | null>(null);
  const { addToast } = useToast();

  const showToast = useCallback(
    (toastConfig: {
      message: string;
      headerText?: string;
      type: "error" | "warning" | "sent";
    }) => {
      const toastKey = `${toastConfig.type}-${toastConfig.message}`;

      if (currentToastKey !== toastKey) {
        addToast(toastConfig);
        setCurrentToastKey(toastKey);
        setTimeout(() => {
          setCurrentToastKey(null);
        }, 3000);
      }
    },
    [currentToastKey, addToast]
  );

  const { mutate: sendInvitationsMutation, isPending } = useSendInvitations({
    onSuccess: () => {
      showToast({
        message: "Invitation has been sent successfully.",
        headerText: "Invitation sent",
        type: "sent",
      });
      setEmails([]);
    },
    onError: (error) => {
      console.log(error);
      showToast({
        headerText: "Failed to send invitations",
        message:
          error?.response?.data.message[0] ||
          "An error occurred while sending invitations.",
        type: "error",
      });
    },
  });

  const { data: organizationsData } = useOrganisation();
  const orgId = organizationsData?.organizations?.[0]?.id || "";

  const { data: singleOrganizationData, refetch } =
    useSingleOrganisation(orgId);

  const { data: students } = useInvitedStudents(
    {
      orgId,
    },
    {
      enabled: !!orgId,
    }
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    if (
      singleOrganizationData?.totalLicenses ===
      singleOrganizationData?.usedLicenses
    ) {
      setNotEnoughLicenses(true);
      // addToast({
      //   message: `The number of invites exceeds the amount of unused licenses.`,
      //   headerText: "Not enough licenses",
      //   type: "error",
      // });
    }

    if (input.includes(",")) {
      const newEmails = input.split(",").map((email) => email.trim());
      setEmails([...emails, ...newEmails]);
      setCurrentToastKey(null);
      refetch();
    } else {
      setInputValue(input);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "," || e.key === "Enter") {
      refetch();
      const email = inputValue.trim();
      if (email && validateEmail(email)) {
        setEmails([...emails, email]);
        setInputValue("");
        setCurrentToastKey(null);
      }
      e.preventDefault();
    } else if (e.key === "Backspace" && inputValue === "") {
      if (emails.length > 0) {
        setEmails(emails.slice(0, -1));
        setCurrentToastKey(null);
      }
      e.preventDefault();
    }
  };

  useEffect(() => {
    const isValidEmail = inputValue.trim() !== "" && validateEmail(inputValue);
    setIsButtonEnabled(emails.length > 0 || isValidEmail);
  }, [emails, inputValue]);

  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }
  }, [emails.length]);

  useEffect(() => {
    const exceedsLicenses =
      singleOrganizationData &&
      emails.length + (inputValue ? 1 : 0) >
        singleOrganizationData?.totalLicenses -
          singleOrganizationData?.usedLicenses;

    if (exceedsLicenses) {
      setNotEnoughLicenses(true);
      if (!hasShownLicenseWarning) {
        showToast({
          message: `The number of invites exceeds the amount of unused licenses.`,
          headerText: "Not enough licenses",
          type: "warning",
        });
        setHasShownLicenseWarning(true);
      }
    } else {
      setNotEnoughLicenses(false);
      setHasShownLicenseWarning(false);
    }
  }, [
    emails,
    singleOrganizationData,
    inputValue,
    hasShownLicenseWarning,
    showToast,
  ]);

  const removeEmail = (index: number) => {
    setEmails(emails.filter((_, i) => i !== index));
    setCurrentToastKey(null);
  };

  const handleInviteClick = () => {
    refetch();
    const email = inputValue.trim();
    const allEmails = email ? [...emails, email] : emails;

    const lowercaseEmails = allEmails.map((e) => e.toLowerCase());
    const existingEmails =
      students?.map((student) => student.email.toLowerCase()) || [];

    const duplicateEmails = lowercaseEmails.filter((email) =>
      existingEmails.includes(email)
    );
    const uniqueEmailsToSend = lowercaseEmails.filter(
      (email) => !duplicateEmails.includes(email)
    );

    if (duplicateEmails.length > 0) {
      showToast({
        headerText: "Some emails are already invited",
        message: `The following emails are already invited: ${duplicateEmails.join(
          ", "
        )}`,
        type: "error",
      });
    }

    if (uniqueEmailsToSend.length > 0 && orgId && !notEnoughLicenses) {
      sendInvitationsMutation(
        {
          emails: Array.from(new Set(uniqueEmailsToSend)),
          orgId,
        },
        {
          onSuccess: () => {
            setInputValue("");
            setEmails([]);
            refetch();
            if (onSuccess) {
              onSuccess();
            }
          },
        }
      );
    }
  };

  return (
    <div className="lg:border-light-grey-10 rounded-20 lg:border px-12 lg:p-24 mb-24 flex-1 relative w-full">
      <GridSMHidden>
        <Typography type="Header3">Invite students</Typography>
        <Typography type="Body2" className="text-dark-grey-5 mt-24">
          Email, comma separated.
        </Typography>
      </GridSMHidden>
      <div
        ref={containerRef}
        className={`border border-light-grey-10 rounded-20 px-12 py-18 mt-24 bg-white lg:bg-transparent overflow-auto max-h-[30svh] hxs:max-h-[40svh] 
        }`}
      >
        <div className="flex flex-wrap gap-12">
          {emails.map((email, index) => (
            <div
              key={index}
              className="flex items-center gap-[4px] rounded-20 border border-light-grey-10 min-h-50 px-8 bg-[#F7F7FD] max-w-full"
            >
              <Typography
                type="Body3"
                className="text-[#868686] break-all flex-1 min-w-0"
              >
                {email}
              </Typography>
              <img
                alt=""
                onClick={() => removeEmail(index)}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                src={hoveredIndex === index ? XIconDarkGrey : XIcon}
                className="cursor-pointer w-24 h-24 shrink-0"
              />
            </div>
          ))}
          <input
            ref={inputRef}
            onFocus={() => {
              const el = containerRef.current;
              if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
            }}
            className="focus:outline-none  ml-12 flex-grow font-medium placeholder-dark-grey-5"
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Type or paste emails here"
          />
        </div>
      </div>
      <div className="flex flex-col gap-14 absolute bottom-24 left-0 lg:px-24 px-12 w-full">
        <Button
          title="Invite"
          buttonType="primary"
          loading={isPending}
          onClick={handleInviteClick}
          disabled={!isButtonEnabled || isPending || notEnoughLicenses}
        />
        <Button
          title="Cancel"
          buttonType="outline"
          styles="!text-primary hover:bg-[#F5F5FC]"
          onClick={cancelClick}
        />
      </div>
    </div>
  );
};

export default InviteStudentsBox;
